/**
 * GBP checkout handoff → USDC.e deposit → spendable card balance.
 *
 * The public on-ramp sends USDC.e on Gnosis to Florin's investment wallet.
 * We preserve the pre-existing round-up reserve, let gas-sponsor maintenance
 * take its one-time $1 network slice, then convert only the remaining deposit
 * to the Safe's configured spend token (GBPe for UK cards).
 */
import { erc20Abi } from "viem";
import { executeCowSwap, waitCowOrder } from "./cow";
import { GNOSIS_SPEND_TOKENS, RESERVE_TOKEN } from "./config";
import { getCardTopUpState, saveCardTopUpState } from "./db";
import { maintainGasBootstrap } from "./gas-sponsor";
import * as gp from "./gnosispay/api";
import { gnosisPublicClient } from "./chains";
import type { CardTopUpState, Hex } from "./types";
import type { LocalWallet } from "./wallet";

/** Avoid treating token dust or an unrelated test transfer as a card top-up. */
const MIN_DEPOSIT_UNITS = 1_000_000n; // 1 USDC.e
const running = new Map<string, Promise<CardTopUpState | undefined>>();

async function tokenBalance(token: Hex, owner: Hex): Promise<bigint> {
  return gnosisPublicClient().readContract({
    address: token,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [owner],
  });
}

function spendToken(symbol: string | null | undefined) {
  const token = GNOSIS_SPEND_TOKENS.find((item) => item.symbol === symbol);
  if (!token) throw new Error(`Unsupported card currency: ${symbol ?? "unknown"}`);
  return token;
}

export async function beginCardTopUp(
  wallet: LocalWallet,
  safeAddress: Hex,
): Promise<CardTopUpState> {
  const existing = await getCardTopUpState(wallet.address);
  if (existing && !["complete", "failed"].includes(existing.status)) return existing;

  const config = await gp.withSession(() => gp.getSafeConfig());
  if (!config.isDeployed || !config.address) throw new Error("Your Gnosis Pay Safe is not deployed");
  if (config.address.toLowerCase() !== safeAddress.toLowerCase()) {
    throw new Error("The signed-in Gnosis Pay Safe does not match Florin's configured card");
  }
  const token = spendToken(config.tokenSymbol);
  const [baselineReserveUnits, safeBalanceBefore] = await Promise.all([
    tokenBalance(RESERVE_TOKEN.address, wallet.address),
    tokenBalance(token.address, safeAddress),
  ]);
  const now = Date.now();
  const state: CardTopUpState = {
    version: 1,
    walletAddress: wallet.address,
    safeAddress,
    status: "awaiting_deposit",
    baselineReserveUnits: baselineReserveUnits.toString(),
    safeTokenAddress: token.address,
    safeTokenSymbol: token.symbol,
    safeBalanceBefore: safeBalanceBefore.toString(),
    startedAt: now,
    updatedAt: now,
  };
  await saveCardTopUpState(state);
  return state;
}

async function runResume(
  wallet: LocalWallet,
  onProgress?: (label: string) => void,
): Promise<CardTopUpState | undefined> {
  let state = await getCardTopUpState(wallet.address);
  if (!state || state.status === "complete") return state;

  try {
    if (state.cowOrderUid && state.cowMinBuy && state.cowValidTo) {
      onProgress?.(`Converting your deposit to ${state.safeTokenSymbol}…`);
      try {
        const received = await waitCowOrder(
          state.cowOrderUid,
          BigInt(state.cowMinBuy),
          state.cowValidTo,
        );
        const complete: CardTopUpState = {
          ...state,
          status: "complete",
          cardReceivedUnits: received.toString(),
          lastError: undefined,
          updatedAt: Date.now(),
        };
        await saveCardTopUpState(complete);
        return complete;
      } catch (error) {
        if (!String(error instanceof Error ? error.message : error).toLowerCase().includes("expired")) {
          throw error;
        }
        // The old order is now cryptographically unable to fill. Clear its
        // artefacts before preparing a fresh quote for the untouched funds.
        state = {
          ...state,
          status: "processing_gas",
          cowOrderUid: undefined,
          cowMinBuy: undefined,
          cowValidTo: undefined,
          lastError: undefined,
          updatedAt: Date.now(),
        };
        await saveCardTopUpState(state);
      }
    }

    const baseline = BigInt(state.baselineReserveUnits);
    const beforeGas = await tokenBalance(RESERVE_TOKEN.address, wallet.address);
    const detected = beforeGas > baseline ? beforeGas - baseline : 0n;
    if (!state.detectedDepositUnits && detected < MIN_DEPOSIT_UNITS) {
      const waiting: CardTopUpState = {
        ...state,
        status: "awaiting_deposit",
        lastError: undefined,
        updatedAt: Date.now(),
      };
      await saveCardTopUpState(waiting);
      return waiting;
    }
    if (!state.detectedDepositUnits) {
      state = {
        ...state,
        status: "processing_gas",
        detectedDepositUnits: detected.toString(),
        lastError: undefined,
        updatedAt: Date.now(),
      };
      await saveCardTopUpState(state);
    }

    onProgress?.("Preparing the deposit…");
    const gas = await maintainGasBootstrap(wallet, state.safeAddress, onProgress);
    if (gas.status !== "repaid" && gas.status !== "self_funded") {
      return state;
    }

    // Re-read after the gas slice and repayment. The difference from the
    // captured baseline is exactly what remains of this top-up for the card.
    const afterGas = await tokenBalance(RESERVE_TOKEN.address, wallet.address);
    const cardSellUnits = afterGas > baseline ? afterGas - baseline : 0n;
    if (cardSellUnits < MIN_DEPOSIT_UNITS) {
      throw new Error("The received amount is too small to convert into card balance");
    }
    state = {
      ...state,
      status: "card_swap_pending",
      cardSellUnits: cardSellUnits.toString(),
      lastError: undefined,
      updatedAt: Date.now(),
    };
    await saveCardTopUpState(state);

    onProgress?.(`Converting your deposit to ${state.safeTokenSymbol}…`);
    const received = await executeCowSwap(
      wallet,
      RESERVE_TOKEN.address,
      state.safeTokenAddress,
      cardSellUnits,
      state.safeAddress,
      onProgress,
      async ({ orderUid, minBuy, validTo }) => {
        state = {
          ...state!,
          cowOrderUid: orderUid,
          cowMinBuy: minBuy.toString(),
          cowValidTo: validTo,
          updatedAt: Date.now(),
        };
        await saveCardTopUpState(state);
      },
    );
    const complete: CardTopUpState = {
      ...state,
      status: "complete",
      cardReceivedUnits: received.toString(),
      lastError: undefined,
      updatedAt: Date.now(),
    };
    await saveCardTopUpState(complete);
    return complete;
  } catch (error) {
    const failed: CardTopUpState = {
      ...state,
      status: "failed",
      lastError: String(error instanceof Error ? error.message : error),
      updatedAt: Date.now(),
    };
    await saveCardTopUpState(failed);
    throw error;
  }
}

export function resumeCardTopUp(
  wallet: LocalWallet,
  onProgress?: (label: string) => void,
): Promise<CardTopUpState | undefined> {
  const key = wallet.address.toLowerCase();
  const active = running.get(key);
  if (active) return active;
  const run = runResume(wallet, onProgress).finally(() => running.delete(key));
  running.set(key, run);
  return run;
}
